import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc' | null;

/**
 * Per-key accessor map for computed/derived columns. The sort value can differ
 * from the raw field — e.g. a formatted "1.2s" duration column sorts by its
 * underlying milliseconds, a "3 MB" bytes column by the raw byte count.
 * Return `null`/`undefined` to push a row to the end regardless of direction.
 */
export type SortAccessors<T> = Record<string, (row: T) => unknown>;

export interface UseTableSortOptions<T> {
  initialKey?: string;
  initialDir?: SortDir;
  /** Map of sortKey -> accessor for derived/computed columns. */
  accessors?: SortAccessors<T>;
}

export interface UseTableSort<T> {
  /** Rows sorted by the active key/dir. When dir is null, original order is preserved. */
  sorted: T[];
  sortKey: string | null;
  sortDir: SortDir;
  /** Cycle a column's sort: asc -> desc -> null (unsorted). */
  toggle: (key: string) => void;
}

const ISO_DATEISH = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}|$)/;

/** True for Date instances or ISO-8601-ish date strings we can compare by time. */
function isDateish(value: unknown): value is string | Date {
  if (value instanceof Date) return true;
  return typeof value === 'string' && ISO_DATEISH.test(value);
}

function toTime(value: string | Date): number {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

/**
 * Compare two non-null values:
 *  - numbers compared numerically,
 *  - date-ish strings / Date by time,
 *  - otherwise String(a).localeCompare(String(b)).
 */
function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (isDateish(a) && isDateish(b)) {
    const ta = toTime(a);
    const tb = toTime(b);
    // Fall through to string compare if either failed to parse.
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
  }
  return String(a).localeCompare(String(b));
}

/**
 * Reusable column-header sort for in-memory tables.
 *
 * Holds `{ key, dir }` state and returns the rows sorted accordingly. Sorting
 * is stable: when `dir` is null the original `rows` order is preserved, and
 * equal keys keep their relative order. null/undefined values always sort last
 * regardless of direction (so the actionable/populated rows lead).
 */
export function useTableSort<T>(
  rows: readonly T[],
  options: UseTableSortOptions<T> = {},
): UseTableSort<T> {
  const { initialKey, initialDir = initialKey ? 'asc' : null, accessors } = options;
  const [sortKey, setSortKey] = useState<string | null>(initialKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initialKey ? initialDir : null);

  const toggle = (key: string): void => {
    if (key !== sortKey) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    // Cycle the active column: asc -> desc -> unsorted.
    if (sortDir === 'asc') {
      setSortDir('desc');
    } else if (sortDir === 'desc') {
      setSortKey(null);
      setSortDir(null);
    } else {
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return rows.slice();

    const accessor = accessors?.[sortKey];
    const valueOf = (row: T): unknown =>
      accessor ? accessor(row) : (row as Record<string, unknown>)[sortKey];

    // Decorate-sort-undecorate to keep the sort stable (Array.prototype.sort is
    // stable per spec, but we also use the index as a tiebreaker for clarity).
    const dirMul = sortDir === 'asc' ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index, value: valueOf(row) }))
      .sort((a, b) => {
        const aNull = a.value == null;
        const bNull = b.value == null;
        // null/undefined always last, regardless of direction.
        if (aNull && bNull) return a.index - b.index;
        if (aNull) return 1;
        if (bNull) return -1;
        const cmp = compareValues(a.value, b.value);
        if (cmp !== 0) return cmp * dirMul;
        return a.index - b.index;
      })
      .map((entry) => entry.row);
  }, [rows, sortKey, sortDir, accessors]);

  return { sorted, sortKey, sortDir, toggle };
}
